import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Theme,
  ThemeContext,
  ThemeColors,
  DEFAULT_THEME_COLORS,
} from '@/context/theme.context';
import { getSettings, updateSettings } from '@/utils/settings';

/** Convert #rrggbb hex to space-separated RGB channels e.g. "238 119 36" */
const hexToChannels = (hex: string): string => {
  const c = hex.replace('#', '');
  return [
    parseInt(c.slice(0, 2), 16),
    parseInt(c.slice(2, 4), 16),
    parseInt(c.slice(4, 6), 16),
  ].join(' ');
};

/** Read the OS/browser dark-mode preference */
const getSystemIsDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const { theme: stored } = getSettings();
    if (stored === 'dark' || stored === 'light' || stored === 'system')
      return stored;
    return 'light';
  });

  // Track actual OS preference so 'system' mode reacts to changes
  const [systemIsDark, setSystemIsDark] = useState(getSystemIsDark);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Resolved theme: what the UI actually shows
  const resolvedIsDark =
    theme === 'dark' || (theme === 'system' && systemIsDark);

  const [colors, setColorsState] = useState<ThemeColors>(() => {
    const { colors: stored } = getSettings();
    return { ...DEFAULT_THEME_COLORS, ...stored };
  });

  // Apply / remove the `dark` class on <html> based on resolved theme
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedIsDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedIsDark]);

  // Persist the user's preference (not the resolved value)
  useEffect(() => {
    updateSettings({ theme });
  }, [theme]);

  // Apply CSS variables for brand colors on <html>
  // Sets both hex vars (for direct use) and RGB channel vars (for Tailwind opacity modifiers)
  useEffect(() => {
    const root = document.documentElement;
    (['primary', 'secondary', 'tertiary'] as const).forEach((key) => {
      root.style.setProperty(`--color-${key}`, colors[key]);
      root.style.setProperty(`--color-${key}-rgb`, hexToChannels(colors[key]));
    });
    updateSettings({ colors });
  }, [colors]);

  /** Cycle: light → dark → system */
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
  }, []);

  const setColors = useCallback((newColors: ThemeColors) => {
    setColorsState(newColors);
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme: resolvedIsDark ? 'dark' : 'light',
        toggleTheme,
        setTheme,
        isDark: resolvedIsDark,
        colors,
        setColors,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
